import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from './authConfig';

let interceptorId;
let responseInterceptorId;
let loginRedirectInProgress = false;

const triggerSessionRefreshLogin = async (msal) => {
  if (loginRedirectInProgress) {
    return;
  }

  loginRedirectInProgress = true;
  window.dispatchEvent(new CustomEvent('icop-auth-expired'));
  await msal.loginRedirect(loginRequest);
};

const resolveActiveAccount = async (msal) => {
  let account = msal.getActiveAccount();
  if (account) {
    return account;
  }

  let accounts = msal.getAllAccounts();
  if (accounts.length > 0) {
    msal.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  // Handle redirect result if app is just returning from login.
  try {
    await msal.handleRedirectPromise();
  } catch (_error) {
    // Ignore here; request interceptor will handle missing account below.
  }

  accounts = msal.getAllAccounts();
  if (accounts.length > 0) {
    msal.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  return null;
};

export const setupAxiosAuth = (msal) => {
  if (interceptorId !== undefined) {
    axios.interceptors.request.eject(interceptorId);
  }
  if (responseInterceptorId !== undefined) {
    axios.interceptors.response.eject(responseInterceptorId);
  }

  interceptorId = axios.interceptors.request.use(
    async (config) => {
      if (config.headers?.Authorization) {
        return config;
      }

      const account = await resolveActiveAccount(msal);
      if (!account) {
        await triggerSessionRefreshLogin(msal);
        return Promise.reject(new Error('No signed-in account available for API request'));
      }

      let tokenResponse;
      try {
        tokenResponse = await msal.acquireTokenSilent({
          ...loginRequest,
          account,
        });
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await triggerSessionRefreshLogin(msal);
          return Promise.reject(error);
        }
        throw error;
      }

      const token = tokenResponse.accessToken || tokenResponse.idToken;
      if (!token) {
        await triggerSessionRefreshLogin(msal);
        return Promise.reject(new Error('Token acquisition returned no token'));
      }

      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${token}`,
      };

      return config;
    },
    (error) => Promise.reject(error)
  );

  responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;

      if (status === 401) {
        const originalRequest = error?.config || {};
        if (!originalRequest.__authRetry) {
          originalRequest.__authRetry = true;

          try {
            const account = await resolveActiveAccount(msal);
            if (account) {
              const tokenResponse = await msal.acquireTokenSilent({
                ...loginRequest,
                account,
                forceRefresh: true,
              });

              const token = tokenResponse.accessToken || tokenResponse.idToken;
              if (token) {
                originalRequest.headers = {
                  ...(originalRequest.headers || {}),
                  Authorization: `Bearer ${token}`,
                };
                return axios(originalRequest);
              }
            }
          } catch (refreshError) {
            if (!(refreshError instanceof InteractionRequiredAuthError)) {
              return Promise.reject(refreshError);
            }
          }
        }

        try {
          await triggerSessionRefreshLogin(msal);
        } catch (redirectError) {
          return Promise.reject(redirectError);
        }
        return Promise.reject(error);
      }

      if (status === 403) {
        const responseData = error?.response?.data || {};
        const serverMessage = responseData.error || 'You do not have access to this application.';
        const debugInfo = responseData.debug || {};

        // The application-level access guard in the API router is the ONLY 403
        // that reports a groupsInToken array, and the only one that means "no
        // access to iCOP at all". Every other 403 is feature-scoped (per-team
        // access checks, admin-only APIs, the batch-remediation role check) and
        // belongs to the caller that made the request.
        //
        // This used to read `debugInfo.groupsInToken || []` and then treat a
        // length of 0 as proof of an empty Azure AD groups claim. Because the
        // feature-scoped responses carry no `debug` object at all, the fallback
        // made every one of them look like a broken token: any denied panel took
        // over the whole app with a full-screen "your session token is missing
        // group information - sign out and sign back in" prompt, which sent users
        // round a re-login loop that could never fix a permission they simply do
        // not have. Only dispatch when the server actually reported the claim.
        const groupsInToken = debugInfo.groupsInToken;
        if (Array.isArray(groupsInToken)) {
          const displayMessage = groupsInToken.length === 0
            ? 'Access denied: Your session token is missing group information. This is a temporary Azure AD issue. Please try: 1) Sign out and sign back in, 2) Clear browser cache, or 3) Use an incognito window. If the issue persists, contact your administrator.'
            : `${serverMessage} Please contact your administrator to request access via MyAccess.`;

          window.dispatchEvent(
            new CustomEvent('icop-auth-denied', {
              detail: {
                message: displayMessage,
                debug: debugInfo
              },
            })
          );
        } else {
          // Feature-scoped denial: surface it to the caller, which renders the
          // message in its own panel, and leave the rest of the app alone.
          console.warn(
            `Access denied for ${error?.config?.url || 'request'}: ${serverMessage}`
          );
        }
      }
      return Promise.reject(error);
    }
  );
};
